import { createElement, type ComponentType, type ReactNode } from "react";

export { loadF7 } from "./load";
export { FoodicsAvatar, FoodicsIcon, FoodicsWordmark } from "./brand";

/**
 * The real F7 components, typed.
 *
 * `f7-demo` is not on a registry this repo can reach, so the design system
 * arrives as a vendored browser bundle (see ./load.ts) and its components live
 * on `window.F7` — which does not exist when this module is evaluated. So each
 * export is a thin wrapper that resolves its component at RENDER time.
 *
 * The wrapper is deliberately transparent: props pass straight through and the
 * children stay in one tree, so F7's internal context (Fieldset/Field ids,
 * Sidebar collapse state, Headless UI state) flows exactly as upstream expects.
 *
 * Types here are hand-written — the DS ships per-component `.d.ts` files but
 * this copy contains only the compiled bundle. They cover the props this
 * console uses, not the full upstream surface. If `f7-demo` ever becomes
 * installable, delete this file and import from it.
 */
function f7<P extends object>(name: string): ComponentType<P> {
  const Wrapped = (props: P) => {
    const C = window.F7?.[name] as ComponentType<P> | undefined;
    if (!C) {
      throw new Error(
        `F7 component "${name}" is not on window.F7. Either loadF7() has not ` +
          `resolved yet, or this name does not exist in the bundle.`,
      );
    }
    return createElement(C, props);
  };
  Wrapped.displayName = `F7.${name}`;
  return Wrapped;
}

// ---------------------------------------------------------------- shared --
type Kids = { children?: ReactNode; className?: string };

// ------------------------------------------------------------ typography --
export const Heading = f7<Kids & { level?: 1 | 2 | 3 | 4 | 5 | 6 }>("Heading");
export const Subheading = f7<Kids & { level?: 1 | 2 | 3 | 4 | 5 | 6 }>("Subheading");
export const Text = f7<Kids>("Text");
export const Strong = f7<Kids>("Strong");
export const Code = f7<Kids>("Code");
export const Divider = f7<{ soft?: boolean; className?: string }>("Divider");
export const TextLink = f7<Kids & { href: string }>("TextLink");
export const Link = f7<Kids & { href: string }>("Link");

// ---------------------------------------------------------- data display --
/**
 * Every colour the bundle's badge map actually ships — checked against it, not
 * copied from upstream's docs.
 *
 * `zinc` used to be here and is not in the map. A colour in the union but not
 * in the bundle typechecks and then renders with no colour classes at all, so
 * it fails as a badge with no background rather than as an error. `zinc` is
 * upstream's neutral; this bundle's is `slate`, which is also the only neutral
 * the console uses. `bundle.test.ts` checks component NAMES against the
 * bundle, not prop values, so nothing catches this class of drift.
 */
export type BadgeColor =
  | "red" | "orange" | "amber" | "yellow" | "lime" | "green" | "emerald" | "teal"
  | "cyan" | "sky" | "blue" | "indigo" | "violet" | "purple" | "fuchsia" | "pink"
  | "rose" | "slate";

export const Badge = f7<Kids & { color?: BadgeColor }>("Badge");
export const BadgeButton = f7<Kids & { color?: BadgeColor; href?: string; onClick?: () => void }>("BadgeButton");
export const Avatar = f7<{ src?: string; initials?: string; alt?: string; square?: boolean; className?: string }>("Avatar");

export const Table = f7<Kids & { striped?: boolean; grid?: boolean; dense?: boolean; bleed?: boolean }>("Table");
export const TableHead = f7<Kids>("TableHead");
export const TableBody = f7<Kids>("TableBody");
export const TableRow = f7<Kids & { href?: string; title?: string; onClick?: () => void }>("TableRow");
export const TableHeader = f7<Kids>("TableHeader");
export const TableCell = f7<Kids>("TableCell");

export const DescriptionList = f7<Kids>("DescriptionList");
export const DescriptionTerm = f7<Kids>("DescriptionTerm");
export const DescriptionDetails = f7<Kids>("DescriptionDetails");

// ----------------------------------------------------------------- forms --
export const Button = f7<
  Kids & {
    /**
     * The solid variants the bundle ships. `dark/zinc` was declared and does
     * not exist in it; `dark/slate` does and was missing from the union — the
     * same zinc-vs-slate drift as BadgeColor above.
     */
    color?: BadgeColor | "dark" | "light" | "dark/slate" | "dark/white" | "white";
    outline?: boolean;
    plain?: boolean;
    disabled?: boolean;
    href?: string;
    type?: "button" | "submit" | "reset";
    onClick?: (e: React.MouseEvent) => void;
    "aria-label"?: string;
  }
>("Button");

export const Fieldset = f7<Kids & { disabled?: boolean }>("Fieldset");
export const Legend = f7<Kids>("Legend");
export const FieldGroup = f7<Kids>("FieldGroup");
export const Field = f7<Kids & { disabled?: boolean }>("Field");
export const Label = f7<Kids>("Label");
export const Description = f7<Kids>("Description");
export const ErrorMessage = f7<Kids>("ErrorMessage");

export const Input = f7<{
  /**
   * React 19 passes `ref` to a function component as an ordinary prop, so it
   * flows through the wrapper into F7's Input and on to the DOM node. Typed
   * here because the wrapper is hand-written — the runtime already forwards it.
   */
  ref?: React.Ref<HTMLInputElement>;
  inputMode?: "text" | "numeric" | "decimal" | "tel" | "search" | "email" | "url" | "none";
  type?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  maxLength?: number;
  dir?: "ltr" | "rtl" | "auto";
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  "aria-label"?: string;
}>("Input");

export const InputGroup = f7<Kids>("InputGroup");

export const Select = f7<
  Kids & {
    name?: string;
    value?: string;
    defaultValue?: string;
    disabled?: boolean;
    invalid?: boolean;
    className?: string;
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    "aria-label"?: string;
  }
>("Select");

export const Textarea = f7<{
  name?: string;
  value?: string;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  maxLength?: number;
  resizable?: boolean;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  "aria-label"?: string;
}>("Textarea");

/** NOTE: onChange receives the next BOOLEAN, not an event. */
export const Switch = f7<{
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  color?: "dark/slate" | "dark/white" | "dark" | "slate" | "white";
  className?: string;
  onChange?: (checked: boolean) => void;
  "aria-label"?: string;
}>("Switch");

export const SwitchField = f7<Kids & { disabled?: boolean }>("SwitchField");
export const SwitchGroup = f7<Kids>("SwitchGroup");
export const Checkbox = f7<{
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  color?: string;
  onChange?: (checked: boolean) => void;
  "aria-label"?: string;
}>("Checkbox");
export const CheckboxField = f7<Kids>("CheckboxField");

// ---------------------------------------------------- overlays & dialogs --
export const Dialog = f7<Kids & { open: boolean; onClose: (v: false) => void; size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" }>("Dialog");
export const DialogTitle = f7<Kids>("DialogTitle");
export const DialogDescription = f7<Kids>("DialogDescription");
export const DialogBody = f7<Kids>("DialogBody");
export const DialogActions = f7<Kids>("DialogActions");

export const Alert = f7<Kids & { open: boolean; onClose: (v: false) => void; size?: string }>("Alert");
export const AlertTitle = f7<Kids>("AlertTitle");
export const AlertDescription = f7<Kids>("AlertDescription");
export const AlertBody = f7<Kids>("AlertBody");
export const AlertActions = f7<Kids>("AlertActions");

export const Dropdown = f7<Kids>("Dropdown");
export const DropdownButton = f7<Kids & { as?: unknown; outline?: boolean; plain?: boolean }>("DropdownButton");
export const DropdownMenu = f7<Kids & { anchor?: string }>("DropdownMenu");
export const DropdownItem = f7<Kids & { href?: string; onClick?: () => void; disabled?: boolean }>("DropdownItem");
export const DropdownLabel = f7<Kids>("DropdownLabel");
export const DropdownDivider = f7<{ className?: string }>("DropdownDivider");

// ------------------------------------------------------------- layout ----
export const Navbar = f7<Kids>("Navbar");
export const NavbarSection = f7<Kids>("NavbarSection");
export const NavbarItem = f7<Kids & { href?: string; current?: boolean; onClick?: () => void; "aria-label"?: string }>("NavbarItem");
export const NavbarLabel = f7<Kids>("NavbarLabel");
export const NavbarDivider = f7<{ className?: string }>("NavbarDivider");
export const NavbarSpacer = f7<{ className?: string }>("NavbarSpacer");

export const Sidebar = f7<Kids>("Sidebar");
export const SidebarHeader = f7<Kids>("SidebarHeader");
export const SidebarBody = f7<Kids>("SidebarBody");
export const SidebarFooter = f7<Kids>("SidebarFooter");
export const SidebarSection = f7<Kids>("SidebarSection");
export const SidebarHeading = f7<Kids>("SidebarHeading");
export const SidebarItem = f7<Kids & { href?: string; current?: boolean; onClick?: () => void }>("SidebarItem");
export const SidebarLabel = f7<Kids>("SidebarLabel");
export const SidebarDivider = f7<{ className?: string }>("SidebarDivider");
export const SidebarSpacer = f7<{ className?: string }>("SidebarSpacer");
export const SidebarRail = f7<Kids & { "aria-label"?: string }>("SidebarRail");
export const SidebarRailItem = f7<Kids & { label?: string; current?: boolean; href?: string; onClick?: () => void }>("SidebarRailItem");
export const SidebarRailDivider = f7<{ className?: string }>("SidebarRailDivider");
export const SidebarPanel = f7<Kids & { "aria-label"?: string }>("SidebarPanel");
export const SidebarPinToggle = f7<{ pinned?: boolean; onToggle?: () => void }>("SidebarPinToggle");
export const SidebarLayout = f7<Kids & { navbar?: ReactNode; sidebar?: ReactNode; collapsed?: boolean; pinned?: boolean; onToggle?: () => void }>("SidebarLayout");
export const StackedLayout = f7<Kids & { navbar?: ReactNode; sidebar?: ReactNode }>("StackedLayout");
